import { Form, InputRef } from 'antd';
import React, { ChangeEvent } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import api from 'api';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { ITariff, QuoteData, Tariffs } from 'components/TariffsComponent/tariffsData';
import { ChoosePayment } from 'components/TariffsComponent/TariffsComponent';
import LicenseTermModal from './LicenseTermModal/LicenseTermModal';
import billing, { paymentUrl } from 'api/billing';
import RoomNameExistsModal from '../RoomNameExistsModal/RoomNameExistsModal';
import checkIsRoomNameExists from 'helpers/checkIsRoomNameExists';
import {
  handleInputChangeWithAltFilter,
  handlePasteWithAltFilter,
} from 'helpers/filterAltCodes';
import { updateRoomStateAfterCreation } from 'store/reducers/userDataCreator';
import BuyTariffModal, { NewRoomFormValues } from './BuyTariffModal';
import CreateRoomModal from './CreateRoomModal';
import PublicOfferModal from './PublicOfferModal/PublicOfferModal';

const isBillingActive =
  window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE'
    ? window.REACT_APP_IS_BILLING_ACTIVE
    : process.env.REACT_APP_IS_BILLING_ACTIVE;
const paymentType =
  window.REACT_APP_PAYMENT_TYPE !== 'REPLACE_REACT_APP_PAYMENT_TYPE'
    ? window.REACT_APP_PAYMENT_TYPE
    : process.env.REACT_APP_PAYMENT_TYPE || 'jusan';

type NewRoomModalProps = {
  open: boolean;
  onCancel: () => void;
  newRoomState?: ChoosePayment;
  mode?: 'single' | 'quota';
};

export default function NewRoomModal({
  open,
  onCancel,
  newRoomState,
  mode,
}: NewRoomModalProps) {
  const [isCreatingRoom, setIsCreatingRoom] = React.useState(false);
  const [isLicenseTermModalOpen, setIsLicenseTermModalOpen] = React.useState<boolean>(false);
  const [isPublicOfferOpen, setIsPublicOfferOpen] = React.useState<boolean>(false);
  const [fields, setFields] = React.useState({ name: '', description: '' });
  const [isRoomNameExistsModalOpen, setIsRoomNameExistsModalOpen] =
    React.useState<boolean>(false);

  const [form] = Form.useForm<NewRoomFormValues>();
  const nameInput = React.useRef<InputRef | null>(null);

  const { messageApi } = useAppSelector((store) => store.windowState);
  const { userData, available_rooms } = useAppSelector(
    (store) => store.userData
  );
  const ended_tariff_lifetime = localStorage.getItem('ended_tariff_lifetime');  
  const billingActive = isBillingActive === 'true' && !userData?.is_service;
  const isPurchaseMode = !!newRoomState;

  const targetRoom = available_rooms.find(
    (room) => room.id === ended_tariff_lifetime
  );
  const nameOfExpiredRoom = targetRoom?.name;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  React.useEffect(() => nameInput.current?.focus(), []);


  const registerTariff = async (tariff: ITariff, quoteName?: string, roomData?: any, isRecurring?: boolean, allow_recurring_payments?: Boolean) => {
    const tariffJSON = {
      tariff_id: tariff.id,
      cost: tariff.tariff_cost,
      constraints: {
        storage: tariff.tariff_constraints.storage,
        members: tariff.tariff_constraints.members,
        ...(tariff.tariff_constraints.rooms && {
          rooms: tariff.tariff_constraints.rooms,
        }),
      },
      payment_type: paymentType,
      room_name: roomData?.name,
      room_description: roomData?.description,
      quote_name: quoteName,
      is_recurring: isRecurring,
      allow_recurring_payments
    };
    const { data: paymentData } = await billing.registerBillV2(tariffJSON);
    return paymentData;
  };

  const checkDuplicateAndOpenModal = (roomName: string) => {
    const isDuplicate = checkIsRoomNameExists(
      roomName,
      available_rooms,
      userData
    );
    if (isDuplicate) setIsRoomNameExistsModalOpen(true);
    return isDuplicate;
  };

  const handleCreateRoom = async (
    value: any,
    quoteName: string,
    quotes: QuoteData[]
  ) => {
    const isDuplicate = checkDuplicateAndOpenModal(value.name);
    if (isDuplicate) return;

    // if (!selectedTariffId || !quotes) {
    //   messageApi?.error(t('Header.error.create'));
    //   return;
    // }

    setIsCreatingRoom(true);
    try {
      const formData = new FormData();

      for (const key in value) {
        key === 'name'
          ? formData.append(key, value[key]?.trim())
          : formData.append(key, value[key]);
      }

      dispatch(setUserAndRoomStatus(''));

      const quote = quotes.find(
        (q) => q.name === quoteName
      );

      if (!quote) {
        throw new Error('Quote not found for selected tariff');
      }

      formData.append('quote_id', quote.id);

      const { data: newRoom } = await api.createRoomV2(formData);

      await dispatch(updateRoomStateAfterCreation({
        newRoom,
        navigate,
        tariffData: {quoteName},
        t,
      })).unwrap();

      setIsCreatingRoom(false);
      onCancel();
    } catch (err: unknown) {
      setIsCreatingRoom(false);
      const details = err instanceof AxiosError && err?.response?.data?.error;

      switch (details) {
        case 'room already exists':
          return messageApi?.warning(t('Header.error.alreadyExists'));
        default:
          return messageApi?.error(t('Header.error.create'));
      }
    }
  };

  const handleSimpleCreateRoom = async (value: any) => {
    const isDuplicate = checkDuplicateAndOpenModal(value.name);
    if (isDuplicate) return;
    setIsCreatingRoom(true);
    try {
      const formData = new FormData();

      for (const key in value) {
        if (value[key]) {
          key === 'name'
            ? formData.append(key, value[key]?.trim())
            : formData.append(key, value[key]);
        }
      }

      dispatch(setUserAndRoomStatus(''));
      const { data: newRoom } = await api.createRoom(formData);

      await dispatch(updateRoomStateAfterCreation({
        newRoom,
        navigate,
        t,
      })).unwrap();

      setIsCreatingRoom(false);
      onCancel();
    } catch (err: unknown) {
      setIsCreatingRoom(false);
      const details = err instanceof AxiosError && err?.response?.data?.error;

      switch (details) {
        case 'room already exists':
          return messageApi?.warning(t('Header.error.alreadyExists'));
        default:
          return messageApi?.error(t('Header.error.create'));
      }
    }
  };

  const handlePurchaseTariff = async (value: NewRoomFormValues) => {
    if (mode !== 'quota') {
      const isDuplicate = checkDuplicateAndOpenModal(value.name);
      if (isDuplicate) return;
    }

    setIsCreatingRoom(true);
    try {
      const formData = new FormData();

      for (const key in value) {
        const field = value[key as keyof NewRoomFormValues];
        if (field === undefined) continue;
        formData.append(key, key === 'name' ? String(field).trim() : String(field));
      }

      dispatch(setUserAndRoomStatus(''));

      if (newRoomState?.tariff?.tariff_name === Tariffs.Trial) {
        const { data: newRoom } = await api.createTrialRoom({
          room_name: value.name,
          room_description: value.description,
        });

        await dispatch(updateRoomStateAfterCreation({
          newRoom,
          tariffData: newRoomState.tariff,
          navigate,
          t,
        })).unwrap();

        setIsCreatingRoom(false);
        onCancel();
        return;
      }

      if (billingActive && newRoomState?.tariff) {
        try {
          const isRecurring = value.payment_option === 'recurring';
          const allow_recurring_payments = value.allow_recurring_payments;
          
          const quoteName = mode === 'quota' ? value.quote_name : undefined;
          const paymentData = await registerTariff(
            newRoomState.tariff,
            quoteName,
            value,
            isRecurring,
            allow_recurring_payments,
          );

          localStorage.setItem(
            `pending_room_creation_${paymentData.quote_id}`,
            JSON.stringify({
              name: value.name,
              description: value.description || '',
              tariff: newRoomState.tariff,
              mode,
              quote_id: paymentData.quote_id,
              is_recurring: isRecurring,
            })
          );

          if (paymentType === 'jusan') {
            let queryParams: string[] = [];
            for (const key in paymentData) {
              const value = paymentData[key] ? paymentData[key] : '';
              queryParams.push(`${key}=${value}`);
            }
            const correctParams = queryParams
              .join('&')
              .replace('&redirect=true', '%26redirect=true');
            window.location.href = `${paymentUrl}?${correctParams}`;
          } else {
            window.location.href = paymentData.payment_link;
          }
        } catch (e) {
          messageApi?.error(t('Header.error.register'));
          setIsCreatingRoom(false);
          onCancel();
        }
      } else {
        const response = await api.getQuotes();
        const quoteId = response.data.find(
          (quote: any) => quote.tariff_plan_id === newRoomState?.tariff?.id
        )?.id;
        formData.append('quote_id', quoteId);
        const { data: newRoom } = await api.createRoomV2(formData);
        await dispatch(updateRoomStateAfterCreation({
          newRoom,
          tariffData: newRoomState?.tariff,
          navigate,
          t,
        })).unwrap();
        setIsCreatingRoom(false);
      }
    } catch (err: unknown) {
      setIsCreatingRoom(false);
      const details =
        (err instanceof AxiosError && err?.response?.data?.error) ||
        newRoomState?.tariff?.tariff_name;

      switch (details) {
        case 'room already exists':
          return messageApi?.warning(t('Header.error.alreadyExists'));
        case 'trial':
          return messageApi?.error(t('Header.error.trialCreate'));
        default:
          return messageApi?.error(t('Header.error.create'));
      }
    }
  };

  const handleTransferToNewQuote = async () => {
    if (!newRoomState?.tariff || !ended_tariff_lifetime) return;
    const formValues = form.getFieldsValue();
    const isRecurring = formValues.payment_option === 'recurring';
    const allow_recurring_payments = formValues.allow_recurring_payments;

    setIsCreatingRoom(true);
    try {
      const paymentData = await registerTariff(
        newRoomState.tariff,
        undefined,
        undefined,
        isRecurring,
        allow_recurring_payments
      );

      localStorage.setItem(
        `pending_room_creation_${paymentData.quote_id}`,
        JSON.stringify({
          mode: 'update_room',
          room_id: ended_tariff_lifetime,
          quote_id: paymentData.quote_id,
          is_recurring: isRecurring,
        })
      );

      if (paymentType === 'jusan') {
        let qveryParams: string[] = [];
        for (const key in paymentData) {
          const value = paymentData[key] ? paymentData[key] : '';
          qveryParams.push(`${key}=${value}`);
        }
        const correctParams = qveryParams
          .join('&')
          .replace('&redirect=true', '%26redirect=true');
        window.location.href = `${paymentUrl}?${correctParams}`;
      } else {
        window.location.href = paymentData.payment_link;
      }
    } catch (e) {
      messageApi?.error(t('Header.error.register'));
      setIsCreatingRoom(false);
      onCancel();
    }
  };

  const modalTitle = (value?: Tariffs) => {
    if (!value) return t('Header.createModal.title');
    if (value === Tariffs.Trial) return t('Header.createModal.trial_title');
    return `${t(`Tariffs.tariff`)} ${t(`Tariffs.tariff_name.${value}`)}`;
  };

  const roomNameLabel = (value?: Tariffs) => {
    switch (value) {
      case Tariffs.Trial:
        return t('Header.createModal.trial_nameLabel');
      default:
        return t('Header.createModal.nameLabel');
    }
  };

  const openLicenseModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    setIsLicenseTermModalOpen!(true);
  };

  const openOfferModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    setIsPublicOfferOpen!(true);
  };

  const onInputsChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const fieldName = event.target.name as keyof NewRoomFormValues;
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      setFields((prev) => ({
        ...prev,
        [fieldName]: filteredValue,
      }));
      form.setFieldValue(fieldName, filteredValue);
    });
  };

  const onInputsPaste = (
    event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const fieldName = (event.target as HTMLInputElement | HTMLTextAreaElement)
      .name as keyof NewRoomFormValues;
    const currentValue = fields[fieldName as keyof typeof fields];

    handlePasteWithAltFilter(event, currentValue, (filteredValue) => {
      setFields((prev) => ({
        ...prev,
        [fieldName]: filteredValue,
      }));
      form.setFieldValue(fieldName, filteredValue);
    });
  };

  const handleNavigateToTariffs = () => {
    onCancel();
    navigate('/tariffs');
  };

  const period =
    newRoomState?.tariff?.tariff_duration_unit?.toLocaleLowerCase();
    
  return (
    <>
      {isPurchaseMode ? (
        <BuyTariffModal
          isOpen={open}
          onCancel={onCancel}
          onSubmit={handlePurchaseTariff}
          onTransferToNewQuote={handleTransferToNewQuote}
          isCreatingRoom={isCreatingRoom}
          form={form}
          nameInput={nameInput}
          fields={fields}
          onInputsChange={onInputsChange}
          onInputsPaste={onInputsPaste}
          newRoomState={newRoomState!}
          ended_tariff_lifetime={ended_tariff_lifetime}
          nameOfExpiredRoom={nameOfExpiredRoom}
          openLicenseModal={openLicenseModal}
          openOfferModal={openOfferModal}
          modalTitle={modalTitle}
          roomNameLabel={roomNameLabel}
          period={period}
          mode={mode}
        />
      ) : (
        <CreateRoomModal
          isOpen={open}
          onCancel={onCancel}
          onSubmit={billingActive ? handleCreateRoom : handleSimpleCreateRoom}
          isCreatingRoom={isCreatingRoom}
          form={form}
          nameInput={nameInput}
          fields={fields}
          onInputsChange={onInputsChange}
          onInputsPaste={onInputsPaste}
          onNavigateToTariffs={handleNavigateToTariffs}
          billingActive={billingActive}
        />
      )}

      {isLicenseTermModalOpen && (
        <React.Suspense fallback={<div />}>
          <LicenseTermModal
            isLicenseTermModalOpen={isLicenseTermModalOpen}
            setIsLicenseTermModalOpen={setIsLicenseTermModalOpen}
            t={t}
            form={form}
          />
        </React.Suspense>
      )}

      {isPublicOfferOpen && (
        <React.Suspense fallback={<div />}>
          <PublicOfferModal
            isPublicOfferOpen={isPublicOfferOpen}
            setIsPublicOfferOpen={setIsPublicOfferOpen}
            t={t}
            form={form}
          />
        </React.Suspense>
      )}

      {isRoomNameExistsModalOpen && (
        <React.Suspense fallback={<div />}>
          <RoomNameExistsModal
            name={form.getFieldValue('name')}
            isOpen={isRoomNameExistsModalOpen}
            onCancel={() => setIsRoomNameExistsModalOpen(false)}
          />
        </React.Suspense>
      )}
    </>
  );
}
